import { useState, useMemo, useCallback } from "react";
import { t } from "../i18n";

export default function UserLibraryModal({
  likedTracks,
  favoritedTracks,
  lang,
  onPlayTrack,
  onToggleLike,
  onToggleFavorite,
  onClose,
  initialTab = "liked",
  onCreatePlaylist,
  onShowPricing,
}) {
  const [tab, setTab] = useState(initialTab);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchDownloading, setBatchDownloading] = useState(false);

  const likedEntries = useMemo(
    () => Array.from(likedTracks.values()),
    [likedTracks]
  );
  const favoritedEntries = useMemo(
    () => Array.from(favoritedTracks.values()),
    [favoritedTracks]
  );

  const entries = tab === "liked" ? likedEntries : favoritedEntries;
  const count = entries.length;

  // Toggle select mode
  const toggleSelectMode = useCallback(() => {
    if (selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    } else {
      setSelectMode(true);
    }
  }, [selectMode]);

  // Toggle individual selection
  const toggleSelect = useCallback((trackId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  }, []);

  // Select all / Deselect all
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(entries.map((e) => e.track.id)));
    }
  }, [entries, selectedIds]);

  // Create playlist from selection
  const handleCreatePlaylist = useCallback(() => {
    if (selectedIds.size === 0) return;
    const trackIds = entries
      .filter((e) => selectedIds.has(e.track.id))
      .map((e) => e.track.id);

    const name = prompt(t(lang, "playlistName"), t(lang, "myLibrary") + " #" + (tab === "liked" ? t(lang, "myLikes") : t(lang, "myFavorites")));
    if (!name || !name.trim()) return;

    onCreatePlaylist(name.trim(), trackIds);
    setSelectMode(false);
    setSelectedIds(new Set());
    onClose();
  }, [selectedIds, entries, lang, tab, onCreatePlaylist, onClose]);

  // Batch download selected tracks
  const handleBatchDownload = useCallback(async () => {
    const token = localStorage.getItem("fm_token");
    if (!token) return;
    const ids = entries
      .filter((e) => selectedIds.has(e.track.id))
      .map((e) => e.track.id);
    if (ids.length === 0) return;

    setBatchDownloading(true);
    try {
      const res = await fetch("/api/tracks/batch-download", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ track_ids: ids }),
      });
      if (res.status === 403) {
        onShowPricing?.();
        return;
      }
      if (!res.ok) throw new Error("Download failed");
      const data = await res.json();
      const urls = data.urls || [];
      urls.forEach((url, i) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = url;
          a.download = `track_${ids[i]}`;
          a.click();
        }, i * 500);
      });
      setSelectMode(false);
      setSelectedIds(new Set());
    } catch (e) {
      console.error("Batch download error:", e);
    } finally {
      setBatchDownloading(false);
    }
  }, [entries, selectedIds, onShowPricing]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {t(lang, "myLibrary")}
          </h2>
          <div className="flex items-center gap-3">
            {count > 0 && !selectMode && (
              <button
                onClick={toggleSelectMode}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium px-3 py-1.5 rounded-lg border border-indigo-200 hover:bg-indigo-50 transition"
              >
                {t(lang, "selectTracks")}
              </button>
            )}
            {selectMode && (
              <button
                onClick={toggleSelectMode}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition"
              >
                {t(lang, "cancel")}
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-8">
          <button
            onClick={() => { setTab("liked"); if (selectMode) { setSelectMode(false); setSelectedIds(new Set()); } }}
            className={`py-3.5 text-sm font-medium transition mr-8 ${
              tab === "liked"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(lang, "myLikes")} ({likedEntries.length})
          </button>
          <button
            onClick={() => { setTab("favorited"); if (selectMode) { setSelectMode(false); setSelectedIds(new Set()); } }}
            className={`py-3.5 text-sm font-medium transition ${
              tab === "favorited"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(lang, "myFavorites")} ({favoritedEntries.length})
          </button>
        </div>

        {/* Track list */}
        <div className="flex-1 overflow-y-auto px-8 py-5 space-y-3 min-h-[240px]">
          {count === 0 ? (
            <div className="flex flex-col items-center justify-center h-56 text-gray-400">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-4">
                {tab === "liked" ? (
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                ) : (
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                )}
              </svg>
              <p className="text-base">{t(lang, tab === "liked" ? "noLikedTracks" : "noFavoritedTracks")}</p>
            </div>
          ) : (
            <>
              {/* Select all / deselect all */}
              {selectMode && (
                <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                  <button
                    onClick={toggleSelectAll}
                    className="text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    {selectedIds.size === entries.length
                      ? t(lang, "cancel")
                      : "全选"}
                  </button>
                  <span>
                    {t(lang, "selectedCount")}: {selectedIds.size} / {entries.length}
                  </span>
                </div>
              )}
              {entries.map((entry, _index) => {
                const { track, scene } = entry;
                const isSelected = selectedIds.has(track.id);
                return (
                  <div
                    key={track.id}
                    className={`flex items-center gap-4 px-4 py-3 rounded-xl transition group ${
                      selectMode && isSelected
                        ? "bg-indigo-50 border border-indigo-200"
                        : selectMode
                        ? "hover:bg-gray-50 border border-transparent hover:border-gray-200"
                        : "hover:bg-gray-50 border border-transparent hover:border-gray-200"
                    }`}
                  >
                    {/* Checkbox (select mode) */}
                    {selectMode && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(track.id)}
                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                      />
                    )}
                    {/* Play button (only in non-select mode) */}
                    {!selectMode && (
                      <button
                        onClick={() => onPlayTrack(track, scene)}
                        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-600 hover:text-indigo-700 transition shadow-sm"
                        title={t(lang, "play")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </button>
                    )}
                    {/* Click area for select mode */}
                    {selectMode && (
                      <div
                        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 cursor-pointer"
                        onClick={() => toggleSelect(track.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </div>
                    )}

                    {/* Track info */}
                    <div className="flex-1 min-w-0" onClick={selectMode ? () => toggleSelect(track.id) : undefined}>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{scene?.emoji || "🎵"}</span>
                        <span className={`text-base font-medium truncate ${selectMode && isSelected ? "text-indigo-700" : "text-gray-800"}`}>
                          {track.name_zh || track.name_en || track.name}
                        </span>
                      </div>
                      <div className={`text-sm mt-1 ${selectMode && isSelected ? "text-indigo-400" : "text-gray-400"}`}>
                        {scene?.name || scene?.nameEn || ""}
                        {track.type && <span className="ml-2">· {t(lang, track.type === "vocal" ? "vocal" : "instrumental")}</span>}
                      </div>
                    </div>

                    {/* Remove button (only in non-select mode) */}
                    {!selectMode && (
                      <button
                        onClick={() => {
                          if (tab === "liked") {
                            onToggleLike(track.id);
                          } else {
                            onToggleFavorite(track.id);
                          }
                        }}
                        className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded text-gray-300 hover:text-red-400 hover:bg-red-50 transition opacity-0 group-hover:opacity-100"
                        title={t(lang, tab === "liked" ? "unlike" : "unfavorite")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Bottom action bar (select mode) */}
        {selectMode && (
          <div className="sticky bottom-0 px-8 py-4 border-t border-gray-200 bg-white rounded-b-xl flex gap-3">
            <button
              onClick={handleCreatePlaylist}
              disabled={selectedIds.size === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {t(lang, "createFromLibrary")} ({selectedIds.size})
            </button>
            <button
              onClick={handleBatchDownload}
              disabled={batchDownloading || selectedIds.size === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {batchDownloading ? t(lang, "downloadInProgress") : `${t(lang, "batchDownload")} (${selectedIds.size})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
